import type { UnifiedThemeOptions } from '@backstage/theme';
import { focusRing, inputBorder, karcher } from './palettes';

const FLAT = { boxShadow: 'none' };

/**
 * `EntityKindPicker` wraps its Select in `<Box pt={1} />`, so the filter column
 * starts one spacing unit below the row. material-table's toolbar ships three,
 * which drops the table title *and* its search field 16px below the first
 * filter label. Matching them lines the two columns up on the catalog and API
 * explorer, which share `CatalogFilterLayout`.
 */
const FILTER_COLUMN_TOP_SPACING = 1;

/**
 * `components` is merged shallowly over `defaultComponentThemes`, so
 * overriding a component replaces Backstage's entry for it entirely. Where a
 * Backstage default exists it is repeated below rather than lost.
 */
export const karcherComponents: UnifiedThemeOptions['components'] = {
  // Kärcher's focus ring: 2px #006EE4 at a 3px offset. ButtonBase covers
  // buttons, tabs, list items, icon buttons and card action areas.
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&:focus-visible': {
          outline: `2px solid ${focusRing}`,
          outlineOffset: 3,
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      // Backstage default: text buttons keep the standard padding.
      text: { padding: undefined },
      root: {
        borderRadius: 0,
        fontWeight: 700,
        // The modern Kärcher system dropped the legacy uppercase button.
        textTransform: 'none',
      },
      contained: FLAT,
      // Kärcher's primary action is a solid black block, inverted on dark.
      containedPrimary: ({ theme }) => ({
        backgroundColor:
          theme.palette.mode === 'dark'
            ? karcher.neutral[50]
            : karcher.neutral[950],
        color:
          theme.palette.mode === 'dark'
            ? karcher.neutral[950]
            : karcher.neutral[0],
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'dark'
              ? karcher.neutral[0]
              : karcher.neutral[1000],
        },
      }),
      // `.btn-border-black`: transparent fill, 2px rule, matching text.
      outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } },
      outlinedPrimary: ({ theme }) => ({
        borderWidth: 2,
        borderColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
        '&:hover': {
          borderWidth: 2,
          borderColor: theme.palette.text.primary,
          backgroundColor: theme.palette.action.hover,
        },
      }),
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: ({ theme }) => ({
        // Backstage default: strips Material UI v5's elevation overlay.
        backgroundImage: 'unset',
        borderRadius: 0,
        border: `1px solid ${theme.palette.divider}`,
      }),
      // Kärcher expresses depth with hairline rules and background-tone steps
      // rather than shadows.
      elevation0: { border: 'none', boxShadow: 'none' },
      elevation1: FLAT,
      elevation2: FLAT,
      elevation3: FLAT,
      elevation4: FLAT,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        // Backstage default: lets card actions settle at the bottom when the
        // card has a forced height.
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 0,
      },
    },
  },
  // `.fc-tile:hover { box-shadow: 0 0 0 3px #ffed00 inset }` — Kärcher signals
  // hover with an inset ring rather than a lift. Scoped to the action area so
  // only genuinely clickable cards get it.
  MuiCardActionArea: {
    styleOverrides: {
      root: {
        '&:hover': { boxShadow: `inset 0 0 0 3px ${karcher.yellow}` },
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      // Kärcher's own #e2e2e2 field borders are 1.3:1 and fail the 3:1
      // threshold for a boundary that is the only thing defining the control.
      notchedOutline: { borderColor: inputBorder },
    },
  },
  BackstageSelectInputBase: {
    styleOverrides: {
      // Backstage hardcodes `1px solid #ced4da` here, ignoring the theme. On
      // the dark surfaces that reads brighter than the cards beside it, and on
      // light it is below the 3:1 boundary threshold. The longhand lands after
      // the shorthand in the merged rule, so it wins.
      input: { borderColor: inputBorder },
    },
  },
  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        borderRadius: 0,
        backgroundColor: karcher.neutral[900],
        color: karcher.neutral[0],
        fontSize: '0.75rem',
      },
    },
  },
  BackstageHeader: {
    styleOverrides: {
      // The dark header already separates itself from the page; the brand rule
      // does the rest.
      header: { boxShadow: 'none' },
    },
  },
  BackstageContentHeader: {
    styleOverrides: {
      // Kärcher's uppercase headline voice, applied only where the string is
      // authored rather than derived from a catalog entity.
      title: {
        textTransform: 'uppercase',
        letterSpacing: '0.02em',
        fontWeight: 900,
      },
    },
  },
  BackstageDismissableBanner: {
    styleOverrides: {
      content: {
        borderRadius: 0,
        '& a': { textDecoration: 'underline' },
      },
    },
  },
  BackstageTableToolbar: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(FILTER_COLUMN_TOP_SPACING, 0, 2.5, 2.5),
      }),
    },
  },
  CatalogReactUserListPicker: {
    styleOverrides: {
      // The picker's Card carries `margin: theme.spacing(1, 0, 1, 0)`. On the
      // API explorer it is the first rendered filter — EntityKindPicker is
      // hidden and EntityTypePicker returns null with no types — so that top
      // margin drops it 8px below the table card beside it. Theme overrides are
      // merged into the component's own rule, so the longhands win over the
      // shorthand and the backgroundColor/boxShadow survive.
      root: ({ theme }) => ({
        marginTop: 0,
        marginBottom: theme.spacing(1),
      }),
    },
  },
};
